import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { getUserInfo, hasPermission, logout } from "../services/auth";
import menuConfig from "../config/menuConfig";
import { useTranslation } from "../i18n/I18nContext";
import { levelKey } from "../utils/classesLabels";
import useIdleLogout from "../hook/useIdleLogout";
import useOnlineStatus from "../hook/useOnlineStatus";
import OfflineBanner from "../components/shared/OfflineBanner";

const LANGUAGES = [
  { code: "pt", label: "Português", flag: "circle-flags:pt" },
  { code: "en", label: "English", flag: "circle-flags:gb" },
];

export const getInitials = (name) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

export const renderMenuIcon = (icon) => {
  if (!icon) return null;
  if (icon.kind === "iconify") {
    return <Icon icon={icon.name} className='menu-icon' />;
  }
  if (icon.kind === "bullet") {
    return <i className={`ri-circle-fill circle-icon ${icon.colorClass} w-auto`} />;
  }
  return <i className={`${icon.className} menu-icon`} />;
};

const MasterLayout = ({ children }) => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation(); // Hook to get the current route
  const navigate = useNavigate();
  const currentUser = getUserInfo();
  const { locale, changeLocale, t } = useTranslation();
  const currentLanguage =
    LANGUAGES.find((lang) => lang.code === locale) || LANGUAGES[0];
  const isOnline = useOnlineStatus();

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    navigate("/sign-in");
  };

  // Termina a sessão automaticamente ao fim de 5 minutos de inactividade.
  useIdleLogout(() => {
    logout();
    navigate("/sign-in", { replace: true, state: { reason: "inactivity" } });
  });

  useEffect(() => {
    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      // Close all dropdowns
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => dropdown.classList.remove("open"));

      // Toggle the clicked dropdown. Expansion/collapse height is handled
      // entirely by CSS (.sidebar-menu .dropdown.open .sidebar-submenu) —
      // do NOT set an inline max-height from submenu.scrollHeight here: that
      // value is measured before things like async Iconify icons finish
      // rendering, so it can end up too small and clip/hide the last
      // submenu item once they load.
      if (!isActive) {
        clickedDropdown.classList.add("open");
      }
    };

    // Attach click event listeners to all dropdown triggers
    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link"
    );

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
        submenuLinks.forEach((link) => {
          // `.active-page` covers "Configurações Gerais"-style items, whose
          // `to` is always the first tab's path (e.g. /configuracoes/multas)
          // even when the active route is one of its other tabs — see
          // isItemActive/subItems above.
          if (
            link.getAttribute("href") === location.pathname ||
            link.getAttribute("to") === location.pathname ||
            link.classList.contains("active-page")
          ) {
            dropdown.classList.add("open");
          }
        });
      });
    };

    // Open the submenu that contains the active route
    openActiveDropdown();

    // Cleanup event listeners on unmount
    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [location.pathname]);

  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  return (
    <section
      className={`${mobileMenu ? "overlay active" : "overlay "}${
        isOnline ? "" : " app-offline"
      }`}
    >
      <OfflineBanner isOnline={isOnline} />
      {/* sidebar */}
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type='button'
          className='sidebar-close-btn'
        >
          <Icon icon='radix-icons:cross-2' />
        </button>
        <div>
          <Link to='/dashboard' className='sidebar-logo'>
            <img
              src='assets/images/logo.png'
              alt='site logo'
              className='light-logo'
            />
            <img
              src='assets/images/logo-light.png'
              alt='site logo'
              className='dark-logo'
            />
            <img
              src='assets/images/logo-icon.png'
              alt='site logo'
              className='logo-icon'
            />
          </Link>
        </div>
        <div className='sidebar-menu-area'>
          <ul className='sidebar-menu' id='sidebar-menu'>
            {menuConfig.map((entry) => {
              if (entry.type === "link") {
                if (!hasPermission(entry.permissionCode)) return null;
                return (
                  <li key={entry.path}>
                    <NavLink
                      to={entry.path}
                      className={(navData) => (navData.isActive ? "active-page" : "")}
                    >
                      {renderMenuIcon(entry.icon)}
                      <span>{t(entry.label)}</span>
                    </NavLink>
                  </li>
                );
              }

              // Most items are visible based on a single permissionCode; an
              // item backed by `subItems` (e.g. "Configurações Gerais",
              // whose 3 codes gate ConfiguracoesLayer's own in-page tabs) is
              // visible instead if the user has ANY of `permissionCodes`.
              const isItemVisible = (item) =>
                item.permissionCodes
                  ? item.permissionCodes.some((code) => hasPermission(code))
                  : hasPermission(item.permissionCode);

              const visibleItems = entry.items.filter(isItemVisible);
              if (visibleItems.length === 0) return null;

              // An item with `subItems` links to one page that owns several
              // in-page tabs/routes (not separate sidebar entries) — treat
              // any of those routes as "active" for it, not just its own
              // `path` (which is only the first tab).
              const isItemActive = (item) =>
                item.subItems
                  ? item.subItems.some((sub) => sub.path === location.pathname)
                  : location.pathname === item.path;

              const itemLabel = (item) => (item.dynamicLevelLabel ? levelKey(item.labelBase) : item.label);

              return (
                <li className='dropdown' key={entry.key}>
                  <Link to='#'>
                    {renderMenuIcon(entry.icon)}
                    <span>{t(entry.label)}</span>
                  </Link>
                  <ul className='sidebar-submenu'>
                    {visibleItems.map((item) => (
                      <li key={item.path}>
                        <NavLink
                          to={item.path}
                          className={isItemActive(item) ? "active-page" : ""}
                        >
                          {renderMenuIcon(item.icon)} {t(itemLabel(item))}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className='navbar-header'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-4'>
                <button
                  type='button'
                  className='sidebar-toggle'
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon='iconoir:arrow-right'
                      className='icon text-2xl non-active'
                    />
                  ) : (
                    <Icon
                      icon='heroicons:bars-3-solid'
                      className='icon text-2xl non-active '
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type='button'
                  className='sidebar-mobile-toggle'
                >
                  <Icon icon='heroicons:bars-3-solid' className='icon' />
                </button>
                <form className='navbar-search'>
                  <input type='text' name='search' placeholder={t('header.search.placeholder')} />
                  <Icon icon='ion:search-outline' className='icon' />
                </form>
              </div>
            </div>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-3'>
                {/* ThemeToggleButton */}
                <ThemeToggleButton />
                {/* Language dropdown */}
                <div className='dropdown'>
                  <button
                    className='w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                    type='button'
                    data-bs-toggle='dropdown'
                  >
                    <Icon icon={currentLanguage.flag} className='text-xl' />
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-sm'>
                    <ul className='to-top-list'>
                      {LANGUAGES.map((lang) => (
                        <li key={lang.code}>
                          <button
                            type='button'
                            onClick={() => changeLocale(lang.code)}
                            className={`dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3 w-100 bg-transparent border-0 text-start${
                              lang.code === locale ? " text-primary-600 fw-semibold" : ""
                            }`}
                          >
                            <Icon icon={lang.flag} className='icon text-xl' />
                            {lang.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {/* Language dropdown end */}
                <div className='dropdown'>
                  <button
                    className='has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center'
                    type='button'
                    data-bs-toggle='dropdown'
                  >
                    <Icon
                      icon='iconoir:bell'
                      className='text-primary-light text-xl'
                    />
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-lg p-0'>
                    <div className='m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                      <div>
                        <h6 className='text-lg text-primary-light fw-semibold mb-0'>
                          {t('header.notifications.title')}
                        </h6>
                      </div>
                    </div>
                    <div className='max-h-400-px overflow-y-auto scroll-sm pe-4 px-24 pb-16 text-secondary-light text-sm'>
                      {t('header.notifications.empty')}
                    </div>
                  </div>
                </div>
                {/* Notification dropdown end */}
                <div className='dropdown'>
                  <button
                    className='d-flex justify-content-center align-items-center rounded-circle'
                    type='button'
                    data-bs-toggle='dropdown'
                  >
                    <span
                      className='w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle bg-primary text-white fw-semibold text-sm'
                    >
                      {getInitials(currentUser?.name)}
                    </span>
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-sm'>
                    <div className='py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                      <div>
                        <h6 className='text-lg text-primary-light fw-semibold mb-2'>
                          {currentUser?.name || "Utilizador"}
                        </h6>
                        <span className='text-secondary-light fw-medium text-sm'>
                          {currentUser?.profile?.description || currentUser?.authority || ""}
                        </span>
                      </div>
                      <button type='button' className='hover-text-danger'>
                        <Icon
                          icon='radix-icons:cross-1'
                          className='icon text-xl'
                        />
                      </button>
                    </div>
                    <ul className='to-top-list'>
                      <li>
                        <Link
                          className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'
                          to='/view-profile'
                        >
                          <Icon
                            icon='solar:user-linear'
                            className='icon text-xl'
                          />{" "}
                          {t('header.profile.myProfile')}
                        </Link>
                      </li>
                      <li>
                        <Link
                          className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3'
                          to='#'
                          onClick={handleLogout}
                        >
                          <Icon icon='lucide:power' className='icon text-xl' />{" "}
                          {t('header.profile.logout')}
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Profile dropdown end */}
              </div>
            </div>
          </div>
        </div>

        {/* dashboard-main-body */}
        <div className='dashboard-main-body'>{children}</div>

        {/* Footer section */}
        <footer className='d-footer'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <p className='mb-0'>{t('footer.copyright', { year: '2024' })}</p>
            </div>
            <div className='col-auto'>
              <p className='mb-0'>
                {t('footer.developedBy')} <span className='text-primary-600'>JUMBATEC LDA</span>
              </p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
};

export default MasterLayout;
